import { useState, useRef, useEffect } from 'react';
import { Mic, Square, Settings } from 'lucide-react';
import { Button } from './ui/Button';
import { API_BASE_URL } from '../lib/api';

interface VoiceRecorderProps {
  onTranscript: (text: string) => void;
  onAudioSaved?: (audioKey: string) => void;
}

type TranscribeMethod = 'webspeech' | 'deepgram';

export function VoiceRecorder({ onTranscript, onAudioSaved }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [method, setMethod] = useState<TranscribeMethod>('webspeech');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Web Speech API の初期化
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = 'ja-JP';
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event: any) => {
          let interim = '';
          let final = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              final += result[0].transcript;
            } else {
              interim += result[0].transcript;
            }
          }

          if (final) {
            setTranscript((prev) => prev + final);
          }
          setInterimTranscript(interim);
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          if (event.error === 'not-allowed') {
            setError('マイクへのアクセスが許可されていません');
          } else {
            setError(`音声認識エラー: ${event.error}`);
          }
          setIsRecording(false);
        };

        recognition.onend = () => {
          if (isRecording && method === 'webspeech') {
            // 自動的に再開
            recognition.start();
          }
        };

        recognitionRef.current = recognition;
      }
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isRecording, method]);

  // 録音開始
  const startRecording = async () => {
    setError('');
    setTranscript('');
    setInterimTranscript('');
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // MediaRecorder でも録音（Deepgram用）
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // 1秒ごとにチャンク

      // Web Speech API で録音開始
      if (method === 'webspeech' && recognitionRef.current) {
        recognitionRef.current.start();
      }

      setIsRecording(true);
    } catch (err: any) {
      setError('マイクへのアクセスに失敗しました');
      console.error('Microphone access error:', err);
    }
  };

  // 録音停止
  const stopRecording = async () => {
    setIsRecording(false);

    if (recognitionRef.current && method === 'webspeech') {
      recognitionRef.current.stop();
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      
      // 全てのトラックを停止
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

      // Deepgramでテキスト化
      if (method === 'deepgram') {
        await processWithDeepgram();
      }
    }

    // 確定したテキストを親コンポーネントに渡す
    if (transcript) {
      onTranscript(transcript);
    }
  };

  // Deepgram でテキスト化
  const processWithDeepgram = async () => {
    if (chunksRef.current.length === 0) return;

    setIsProcessing(true);
    setError('');

    try {
      const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await fetch(`${API_BASE_URL}/api/upload/transcribe`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'テキスト化に失敗しました');
      }

      setTranscript(data.transcript || '');
      onTranscript(data.transcript || '');
      
      if (data.audioKey && onAudioSaved) {
        onAudioSaved(data.audioKey);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // テキストを適用
  const handleApply = () => {
    const fullTranscript = transcript + interimTranscript;
    if (fullTranscript) {
      onTranscript(fullTranscript);
    }
  };

  const isWebSpeechSupported = typeof window !== 'undefined' && 
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  return (
    <div className="space-y-4">
      {/* コントロール */}
      <div className="flex items-center gap-3">
        {!isRecording ? (
          <Button
            onClick={startRecording}
            disabled={isProcessing}
            leftIcon={<Mic size={18} />}
            className="bg-red-500 hover:bg-red-600"
          >
            録音開始
          </Button>
        ) : (
          <Button
            onClick={stopRecording}
            variant="danger"
            leftIcon={<Square size={18} />}
          >
            録音停止
          </Button>
        )}

        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}
          title="設定"
        >
          <Settings size={20} />
        </button>

        {isRecording && (
          <span className="flex items-center gap-2 text-red-500 animate-pulse">
            <span className="w-3 h-3 bg-red-500 rounded-full" />
            録音中...
          </span>
        )}

        {isProcessing && (
          <span className="text-blue-500">テキスト化処理中...</span>
        )}
      </div>

      {/* 設定 */}
      {showSettings && (
        <div 
          className="p-4 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--light-gray-primary)',
            borderColor: 'var(--border-color)',
          }}
        >
          <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            テキスト化方式
          </p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="method"
                value="webspeech"
                checked={method === 'webspeech'}
                onChange={() => setMethod('webspeech')}
                disabled={!isWebSpeechSupported}
                className="text-blue-600"
              />
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Web Speech API
                <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>
                  (リアルタイム・無料)
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="method"
                value="deepgram"
                checked={method === 'deepgram'}
                onChange={() => setMethod('deepgram')}
                className="text-blue-600"
              />
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Deepgram
                <span className="text-xs ml-1" style={{ color: 'var(--text-secondary)' }}>
                  (高精度)
                </span>
              </span>
            </label>
          </div>
          {!isWebSpeechSupported && (
            <p className="text-xs text-orange-500 mt-2">
              このブラウザはWeb Speech APIをサポートしていません
            </p>
          )}
        </div>
      )}

      {/* エラー */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* プレビュー */}
      {(transcript || interimTranscript) && (
        <div className="space-y-2">
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            テキスト化結果
          </p>
          <div 
            className="p-4 rounded-lg border min-h-[100px]"
            style={{
              backgroundColor: 'var(--background-card)',
              borderColor: 'var(--border-color)',
            }}
          >
            <p style={{ color: 'var(--text-primary)' }}>
              {transcript}
              <span className="text-gray-400">{interimTranscript}</span>
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleApply} size="sm">
              テキストエリアに適用
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
